import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { 
  OperadorParaDragDrop, 
  UsuarioConOperadores, 
  RespuestaDatosDragDropOperadores 
} from '../../../services/asignacionesUsuarioOperadorService';

// ===============================
// TIPOS PARA EL ESTADO
// ===============================

export interface DragDropOperatorState {
  // Datos principales
  usuariosConOperadores: UsuarioConOperadores[];
  operadoresDisponibles: OperadorParaDragDrop[];
  organizacionId: number;
  
  // Estado de la UI
  isLoading: boolean;
  isDragging: boolean;
  hasChanges: boolean;
  
  // Estadísticas
  totalOperadores: number;
  operadoresAsignados: number;
  totalUsuarios: number;
  
  // Estados de operación
  isSaving: boolean;
  saveError: string | null;
  lastSaved: Date | null;
}

export type DragDropOperatorAction =
  | { type: 'SET_INITIAL_DATA'; payload: RespuestaDatosDragDropOperadores }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DRAGGING'; payload: boolean }
  | { type: 'UPDATE_AVAILABLE_OPERATORS'; payload: OperadorParaDragDrop[] }
  | { type: 'MOVE_OPERATOR_TO_USER'; payload: { operadorId: number; usuarioId: number } }
  | { type: 'MOVE_OPERATOR_TO_AVAILABLE'; payload: { operadorId: number; fromUserId: number } }
  | { type: 'MOVE_OPERATOR_BETWEEN_USERS'; payload: { operadorId: number; fromUserId: number; toUserId: number } }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_SAVE_ERROR'; payload: string | null }
  | { type: 'MARK_SAVED'; payload: Date }
  | { type: 'RESET_CHANGES' };

// ===============================
// ESTADO INICIAL
// ===============================

const initialState: DragDropOperatorState = {
  usuariosConOperadores: [],
  operadoresDisponibles: [],
  organizacionId: 0,
  isLoading: true,
  isDragging: false,
  hasChanges: false,
  totalOperadores: 0,
  operadoresAsignados: 0,
  totalUsuarios: 0,
  isSaving: false,
  saveError: null,
  lastSaved: null,
};

// ===============================
// REDUCER
// ===============================

function dragDropOperatorReducer(state: DragDropOperatorState, action: DragDropOperatorAction): DragDropOperatorState {
  switch (action.type) {
    case 'SET_INITIAL_DATA':
      return {
        ...state,
        usuariosConOperadores: action.payload.usuarios_con_operadores,
        operadoresDisponibles: action.payload.operadores_disponibles,
        organizacionId: action.payload.organizacion_id,
        totalOperadores: action.payload.total_operadores,
        operadoresAsignados: action.payload.operadores_asignados,
        totalUsuarios: action.payload.total_usuarios,
        isLoading: false,
        hasChanges: false,
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_DRAGGING':
      return { ...state, isDragging: action.payload };

    case 'UPDATE_AVAILABLE_OPERATORS':
      return { ...state, operadoresDisponibles: action.payload };

    case 'MOVE_OPERATOR_TO_USER': {
      const { operadorId, usuarioId } = action.payload;
      
      // Encontrar el operador en disponibles
      const operador = state.operadoresDisponibles.find(o => o.id === operadorId);
      if (!operador) return state;

      // Remover de disponibles
      const nuevosOperadoresDisponibles = state.operadoresDisponibles.filter(o => o.id !== operadorId);

      // Agregar al usuario (crear usuario si no existe)
      let nuevosUsuarios = [...state.usuariosConOperadores];
      const usuarioIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === usuarioId);

      if (usuarioIndex >= 0) {
        // Usuario existe, agregar operador
        nuevosUsuarios[usuarioIndex] = {
          ...nuevosUsuarios[usuarioIndex],
          operadores: [...nuevosUsuarios[usuarioIndex].operadores, operador]
        };
      } else {
        // Usuario no existe, crear nuevo
        nuevosUsuarios.push({
          usuario_uau_id: usuarioId,
          operadores: [operador]
        });
      }

      return {
        ...state,
        usuariosConOperadores: nuevosUsuarios,
        operadoresDisponibles: nuevosOperadoresDisponibles,
        operadoresAsignados: state.operadoresAsignados + 1,
        hasChanges: true,
      };
    }

    case 'MOVE_OPERATOR_TO_AVAILABLE': {
      const { operadorId, fromUserId } = action.payload;
      
      // Encontrar usuario y operador
      const usuario = state.usuariosConOperadores.find(u => u.usuario_uau_id === fromUserId);
      const operador = usuario?.operadores.find(o => o.id === operadorId);
      if (!operador) return state;

      // Remover operador del usuario
      let nuevosUsuarios = state.usuariosConOperadores.map(u => {
        if (u.usuario_uau_id === fromUserId) {
          const nuevosOperadores = u.operadores.filter(o => o.id !== operadorId);
          return { ...u, operadores: nuevosOperadores };
        }
        return u;
      }).filter(u => u.operadores.length > 0); // Remover usuarios sin operadores

      // Agregar a disponibles
      const nuevosOperadoresDisponibles = [...state.operadoresDisponibles, operador];

      return {
        ...state,
        usuariosConOperadores: nuevosUsuarios,
        operadoresDisponibles: nuevosOperadoresDisponibles,
        operadoresAsignados: state.operadoresAsignados - 1,
        totalUsuarios: nuevosUsuarios.length,
        hasChanges: true,
      };
    }

    case 'MOVE_OPERATOR_BETWEEN_USERS': {
      const { operadorId, fromUserId, toUserId } = action.payload;
      
      // Encontrar el operador en el usuario origen
      const fromUser = state.usuariosConOperadores.find(u => u.usuario_uau_id === fromUserId);
      const operador = fromUser?.operadores.find(o => o.id === operadorId);
      if (!operador) return state;

      let nuevosUsuarios = [...state.usuariosConOperadores];

      // Remover del usuario origen
      const fromIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === fromUserId);
      if (fromIndex >= 0) {
        nuevosUsuarios[fromIndex] = {
          ...nuevosUsuarios[fromIndex],
          operadores: nuevosUsuarios[fromIndex].operadores.filter(o => o.id !== operadorId)
        };
      }

      // Agregar al usuario destino
      const toIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === toUserId);
      if (toIndex >= 0) {
        nuevosUsuarios[toIndex] = {
          ...nuevosUsuarios[toIndex],
          operadores: [...nuevosUsuarios[toIndex].operadores, operador]
        };
      } else {
        // Usuario destino no existe, crear nuevo
        nuevosUsuarios.push({
          usuario_uau_id: toUserId,
          operadores: [operador]
        });
      }

      // Filtrar usuarios sin operadores
      nuevosUsuarios = nuevosUsuarios.filter(u => u.operadores.length > 0);

      return {
        ...state,
        usuariosConOperadores: nuevosUsuarios,
        totalUsuarios: nuevosUsuarios.length,
        hasChanges: true,
      };
    }

    case 'SET_SAVING':
      return { ...state, isSaving: action.payload, saveError: null };

    case 'SET_SAVE_ERROR':
      return { ...state, saveError: action.payload, isSaving: false };

    case 'MARK_SAVED':
      return { 
        ...state, 
        hasChanges: false, 
        isSaving: false, 
        saveError: null, 
        lastSaved: action.payload 
      };

    case 'RESET_CHANGES':
      return { ...state, hasChanges: false, saveError: null };

    default:
      return state;
  }
}

// ===============================
// CONTEXTO
// ===============================

interface DragDropOperatorContextType {
  state: DragDropOperatorState;
  dispatch: React.Dispatch<DragDropOperatorAction>;
  
  // Acciones auxiliares
  moveOperatorToUser: (operadorId: number, usuarioId: number) => void;
  moveOperatorToAvailable: (operadorId: number, fromUserId: number) => void;
  moveOperatorBetweenUsers: (operadorId: number, fromUserId: number, toUserId: number) => void;
  
  // Utilidades
  getOperatorById: (id: number) => OperadorParaDragDrop | undefined;
  getUserOperators: (usuarioId: number) => OperadorParaDragDrop[];
  hasUserOperator: (usuarioId: number, operadorId: number) => boolean;
}

const DragDropOperatorContext = createContext<DragDropOperatorContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================

interface DragDropOperatorProviderProps {
  children: ReactNode;
}

export function DragDropOperatorProvider({ children }: DragDropOperatorProviderProps) {
  const [state, dispatch] = useReducer(dragDropOperatorReducer, initialState);

  // Acciones auxiliares
  const moveOperatorToUser = (operadorId: number, usuarioId: number) => {
    dispatch({ type: 'MOVE_OPERATOR_TO_USER', payload: { operadorId, usuarioId } });
  };

  const moveOperatorToAvailable = (operadorId: number, fromUserId: number) => {
    dispatch({ type: 'MOVE_OPERATOR_TO_AVAILABLE', payload: { operadorId, fromUserId } });
  };

  const moveOperatorBetweenUsers = (operadorId: number, fromUserId: number, toUserId: number) => {
    dispatch({ type: 'MOVE_OPERATOR_BETWEEN_USERS', payload: { operadorId, fromUserId, toUserId } });
  };

  // Utilidades
  const getOperatorById = (id: number): OperadorParaDragDrop | undefined => {
    // Buscar en disponibles
    const disponible = state.operadoresDisponibles.find(o => o.id === id);
    if (disponible) return disponible;

    // Buscar en asignados
    for (const usuario of state.usuariosConOperadores) {
      const operador = usuario.operadores.find(o => o.id === id);
      if (operador) return operador;
    }

    return undefined;
  };

  const getUserOperators = (usuarioId: number): OperadorParaDragDrop[] => {
    const usuario = state.usuariosConOperadores.find(u => u.usuario_uau_id === usuarioId);
    return usuario?.operadores || [];
  };

  const hasUserOperator = (usuarioId: number, operadorId: number): boolean => {
    const usuario = state.usuariosConOperadores.find(u => u.usuario_uau_id === usuarioId);
    return usuario?.operadores.some(o => o.id === operadorId) || false;
  };

  const contextValue: DragDropOperatorContextType = {
    state,
    dispatch,
    moveOperatorToUser,
    moveOperatorToAvailable,
    moveOperatorBetweenUsers,
    getOperatorById,
    getUserOperators,
    hasUserOperator,
  };

  return (
    <DragDropOperatorContext.Provider value={contextValue}>
      {children}
    </DragDropOperatorContext.Provider>
  );
}

// ===============================
// HOOK
// ===============================

export function useDragDropOperator() {
  const context = useContext(DragDropOperatorContext);
  if (context === undefined) {
    throw new Error('useDragDropOperator debe usarse dentro de un DragDropOperatorProvider');
  }
  return context;
}
